/**
 * `Ф0` (кабинет ПЗС): формат чисел, времени и дат — по макету «Система».
 *
 * Цифры — табличные, разделитель тысяч — пробел, дробной части — запятая
 * (`font-variant-numeric: tabular-nums` ставится классом `.cabinet-num`).
 * Одно место на весь кабинет: иначе шапка, карточки и выгрузка показывали бы
 * одно и то же число по-разному.
 */

/** Число по-русски: `12 486,3`. Пустое значение — прочерк, а не ноль. */
export function formatNumber(
  value: number | string | null | undefined,
  { maximumFractionDigits = 1 } = {},
): string {
  if (value === null || value === undefined || value === "") return "—";

  const numeric = Number(value);

  if (!Number.isFinite(numeric)) return "—";

  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  })
    .format(numeric)
    // `Intl` ставит неразрывный пробел; в макете — обычный, и он же нужен
    // тестам и выгрузке: иначе одно и то же число выглядит по-разному.
    .replace(/\u00a0/g, " ");
}

/** Время `ЧЧ:ММ:СС`; без значения — прочерк. */
export function formatTime(value: Date | string | null | undefined): string {
  if (!value) return "—";

  const at = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(at.getTime())) return "—";

  const pad = (part: number) => String(part).padStart(2, "0");

  return `${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
}

/** Дата `ДД.ММ.ГГГГ`; без значения — прочерк. */
export function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "—";

  const at = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(at.getTime())) return "—";

  const pad = (part: number) => String(part).padStart(2, "0");

  return `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()}`;
}

/** ФИО из полей профиля: «Петрова Анна Ивановна», иначе почта, иначе прочерк. */
export function formatFullName(user: {
  lastName?: string | null;
  firstName?: string | null;
  surName?: string | null;
  email?: string | null;
} | null | undefined): string {
  if (!user) return "—";

  const full = [user.lastName, user.firstName, user.surName]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");

  return full || user.email || "—";
}

/** Инициалы для кружка: «ПА» из «Петрова Анна». */
export function initialsOf(user: {
  lastName?: string | null;
  firstName?: string | null;
  email?: string | null;
} | null | undefined): string {
  if (!user) return "—";

  const first = (user.lastName || user.email || "?").trim().charAt(0);
  const second = (user.firstName || "").trim().charAt(0);

  return `${first}${second}`.toUpperCase() || "?";
}

/** Дата и время `ДД.ММ.ГГГГ ЧЧ:ММ` — для показаний и реквизитов. */
export function formatDateTime(value: Date | string | null | undefined): string {
  if (!value) return "—";

  const at = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(at.getTime())) return "—";

  const pad = (part: number) => String(part).padStart(2, "0");

  return (
    `${pad(at.getDate())}.${pad(at.getMonth() + 1)}.${at.getFullYear()} ` +
    `${pad(at.getHours())}:${pad(at.getMinutes())}`
  );
}
